import { z } from 'zod';

// The backend emits an arbitrary role slug (superadmin | admin | member | custom).
// Kept as a free string so a real custom-role login parses rather than throwing.
// It is a display LABEL only — capability comes from the permission keys.
export const meUserSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  name: z.string(),
  role: z.string(),
  avatarUrl: z.string().nullable().optional(),
  title: z.string().nullable().optional(),
  scopedWorkspaceId: z.string().nullable().optional(),
});

// The server returns `{ user, permissions, assignments }`. `permissions` is the
// flat effective list (e.g. ['users.manage','audit.read', …]). It's optional so
// older payloads still parse.
// When the session is an impersonation (an admin acting AS this user), the
// server returns the original actor here so the UI can show the banner.
export const impersonatorSchema = z.object({ id: z.string(), name: z.string() });

export const meResponseSchema = z.object({
  user: meUserSchema,
  permissions: z.array(z.string()).optional(),
  // FIX(admin-perms-platform-surface): every key `requirePlatformPerm` grants
  // this user (server `platformSurfacePermissions`). Optional — an older server
  // omits it and nothing from the admin surface is granted.
  platformSurfacePermissions: z.array(z.string()).optional(),
  impersonator: impersonatorSchema.nullish(),
  // FIX(admin-tracker-cross-org): the caller's active organisation (server
  // `/me` → `activeWorkspaceId`), so the tracker no-access copy can tell "this
  // is another org's project" from "your own org, but no project access".
  // Optional — an older server omits it.
  activeWorkspaceId: z.string().nullish(),
});

/**
 * FIX(admin-login-2fa): the second shape `POST /api/auth/login` can answer with.
 * When email 2FA applies (the user opted in, or an org mandates it) and this
 * browser holds no trusted-device grant, the server verifies the password but
 * issues NO session — it mails a code and returns this challenge handle instead
 * (server `SessionController.login` → `TwoFactorService.issue`). Before: login
 * parsed only the session shape, so this threw and showed "Sign in failed".
 * After: `parseLoginResponse` branches on `twoFactorRequired`.
 */
export const twoFactorChallengeSchema = z.object({
  twoFactorRequired: z.literal(true),
  /** Opaque, single-use handle — the only authority for `/login/2fa`. */
  challengeId: z.string().min(1),
  /** Epoch ms the code stops working. */
  expiresAt: z.number(),
  /** Epoch ms a resend becomes available (the cooldown). */
  resendAvailableAt: z.number(),
  /** `ad***@example.com` — confirms which inbox to check. */
  maskedEmail: z.string(),
  codeLength: z.number().int().positive().default(6),
});
export type TwoFactorChallenge = z.infer<typeof twoFactorChallengeSchema>;

/** Kept for callers that only ever expect a session (e.g. `/login/2fa`). */
export const loginResponseSchema = meResponseSchema;

export type MeUser = z.infer<typeof meUserSchema>;
export type Impersonator = z.infer<typeof impersonatorSchema>;

export interface AdminUser {
  id: string;
  email: string;
  name: string;
  /**
   * The RAW global role slug from the server (`superadmin`, `admin`, `member`,
   * a custom slug) — a display LABEL only. FIX(perm-admin-keys-only): never
   * authority; `lib/permissions` reads permission keys, not this.
   */
  serverRole: string;
  avatarUrl: string | null;
  title: string | null;
  scopedWorkspaceId?: string;
  /** Flat effective server permissions (authoritative). */
  permissions: string[];
  /**
   * Did the server send a permission list at all? A payload WITHOUT the field
   * (a legacy server) grants nothing, exactly like a present-but-empty list.
   */
  permissionsKnown: boolean;
  /**
   * FIX(admin-perms-platform-surface): the keys the PLATFORM surface
   * (`/api/admin/*`) grants this user, straight from the server's
   * `hasPlatformSurfacePerm`. `null` when the server did not send the field
   * (older server) — then no admin-surface org key is granted.
   */
  platformSurfacePermissions: string[] | null;
  /**
   * FIX(admin-impersonation-banner-source): the original actor when this session
   * is an impersonation (server `/me` → `impersonator`), else null. The banner
   * is driven from this server truth. Before: `toAdminUser` dropped the field and
   * the banner read a localStorage store that could disagree with the cookie.
   */
  impersonator: Impersonator | null;
  /**
   * FIX(admin-tracker-cross-org): the active organisation the session's
   * org-scoped reads resolve against (server `/me` → `activeWorkspaceId`), or
   * null (no org / older server).
   */
  activeWorkspaceId: string | null;
}

export function toAdminUser(
  u: MeUser,
  permissions?: string[] | null,
  impersonator?: Impersonator | null,
  platformSurfacePermissions?: string[] | null,
  activeWorkspaceId?: string | null,
): AdminUser {
  return {
    id: u.id,
    email: u.email,
    name: u.name,
    serverRole: u.role,
    avatarUrl: u.avatarUrl ?? null,
    title: u.title ?? null,
    scopedWorkspaceId: u.scopedWorkspaceId ?? undefined,
    permissions: permissions ?? [],
    permissionsKnown: Array.isArray(permissions),
    platformSurfacePermissions: Array.isArray(platformSurfacePermissions) ? platformSurfacePermissions : null,
    impersonator: impersonator ?? null,
    activeWorkspaceId: activeWorkspaceId ?? null,
  };
}

/** A parsed `/me` (or session-issuing) payload → AdminUser. */
export function adminUserFromSession(raw: unknown): AdminUser {
  const parsed = meResponseSchema.parse(raw);
  return toAdminUser(
    parsed.user,
    parsed.permissions,
    parsed.impersonator,
    parsed.platformSurfacePermissions,
    parsed.activeWorkspaceId,
  );
}

/** What `POST /api/auth/login` resolved to. */
export type LoginResult =
  | { kind: 'session'; user: AdminUser }
  | { kind: 'twoFactor'; challenge: TwoFactorChallenge };

/** Branch a login response: a 2FA challenge (no session yet) or a session. */
export function parseLoginResponse(raw: unknown): LoginResult {
  if (raw && typeof raw === 'object' && (raw as { twoFactorRequired?: unknown }).twoFactorRequired === true) {
    return { kind: 'twoFactor', challenge: twoFactorChallengeSchema.parse(raw) };
  }
  return { kind: 'session', user: adminUserFromSession(raw) };
}

export const loginInputSchema = z.object({
  email: z.string().email('Enter a valid email'),
  password: z.string().min(1, 'Password is required'),
});
export type LoginInput = z.infer<typeof loginInputSchema>;
